<script setup lang="ts">
import { cn } from "@voltalis/frontend-utils";
import { cloneDeep } from "es-toolkit";
import { ref } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { SiteSectionId } from "@/data/common/ids";
import { SectionForest } from "@/domains/v4b/site-section/site-section.types";
import { useTypedIntl } from "@/locales/typed-intl";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

import SimpleSiteSectionsTree from "@/app/tools/new-section-management/site-sections-tree/tree-display/simple-site-sections-tree.vue";
import DropButton from "@/components/ui/buttons/drop-button.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const props = defineProps<{
  siteName: string | undefined;
  siteSectionsForest: SectionForest | undefined;
  isLoading?: boolean;
  initialSelectedSections?: Map<SiteSectionId, string>;
}>();
const emits = defineEmits<{
  (e: "updateSelection", updatedSelection: Map<SiteSectionId, string>): void;
}>();
defineSlots<{
  buttonText(): void;
}>();

const { t, k } = useTypedIntl();

const isDropButtonShown = ref<boolean>(false);

const sectionSearchQuery = ref<string>("");

const selectedSections = ref<Map<SiteSectionId, string>>(new Map(props.initialSelectedSections) ?? []);

function resetSelectedSections() {
  selectedSections.value = new Map();
}

function handleSubmitSelectedSections(callback: () => void) {
  emits("updateSelection", selectedSections.value);
  callback();
}

function handleDropButtonHide() {
  isDropButtonShown.value = false;
  if (!props.initialSelectedSections) {
    resetSelectedSections();
    return;
  }

  selectedSections.value = cloneDeep(props.initialSelectedSections);
}
</script>

<template>
  <DropButton
    :data-test-id="DATA_TEST_IDS.components.selectSiteSectionPerimeter.component"
    @hide="handleDropButtonHide"
    @show="isDropButtonShown = true"
  >
    <template #trigger>
      <button
        class="group flex gap-3 items-center h-[39px]"
        :data-test-id="DATA_TEST_IDS.components.selectSiteSectionPerimeter.triggerButton"
      >
        <h3
          class="group-hover:underline"
          :data-test-id="DATA_TEST_IDS.components.selectSiteSectionPerimeter.triggerButtonText"
        >
          <slot name="buttonText">{{
            t(k.data.consumptionMonitoring.siteMonitoring.sectionPerimeter.selectPerimeter)
          }}</slot>
        </h3>

        <i
          :class="cn('pi pi-angle-down transition-all delay-75 text-xs', { 'rotate-180': isDropButtonShown })"
          :data-test-id="DATA_TEST_IDS.components.selectSiteSectionPerimeter.triggerButtonAngleIcon"
        />
      </button>
    </template>

    <template #content="{ hideContent: hideDropContent }">
      <div class="w-[325px] p-4 flex flex-col h-[364px] gap-4">
        <IconField class="w-full">
          <InputIcon class="pi pi-search" />
          <InputText
            v-model="sectionSearchQuery"
            :data-test-id="DATA_TEST_IDS.components.selectSiteSectionPerimeter.searchSectionInputText"
            class="!h-[38px]"
            :placeholder="t(k.tools.siteManagement.siteTreeStructure.searchSection)"
            @update:modelValue="resetSelectedSections"
          />
        </IconField>

        <SimpleSiteSectionsTree
          v-model:search-query="sectionSearchQuery"
          v-model:selected-sections="selectedSections"
          class="flex h-full w-full"
          :is-loading="props.isLoading"
          selectable
          :site-name="props.siteName"
          :site-sections-forest="props.siteSectionsForest"
        />

        <div class="flex gap-2 w-full">
          <HospitalityButton
            class="flex flex-grow"
            :data-test-id="DATA_TEST_IDS.components.selectSiteSectionPerimeter.cancelButton"
            :label="t(k.global.misc.cancel)"
            severity="secondary"
            size="xs"
            @click="hideDropContent"
          />
          <HospitalityButton
            class="flex flex-grow"
            :data-test-id="DATA_TEST_IDS.components.selectSiteSectionPerimeter.submitButton"
            :label="t(k.global.misc.submit)"
            size="xs"
            @click="handleSubmitSelectedSections(hideDropContent)"
          />
        </div>
      </div>
    </template>
  </DropButton>
</template>
